import * as THREE from '/assets/scripts/three.module.js';

const LIGHT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-sun-moon-svg"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
const DARK_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-sun-moon-svg"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;

export function createEnvironmentController({
  getScene,
  getLights,
  disposeObject3DResources,
  markRenderDirty,
  markShadowDirty
} = {}) {
  let isLightMode = false;
  let currentDeskSize = '1400x700';
  let deskGroup = null;
  let floorGrid = null;

  function renderDeskSurfaceEnvironment() {
    const scene=getScene?.();
    if (!scene) return;
    if (deskGroup) {
      scene.remove(deskGroup);
      disposeObject3DResources?.(deskGroup);
      deskGroup = null;
    }
    if (floorGrid) {
      scene.remove(floorGrid);
      disposeObject3DResources?.(floorGrid);
      floorGrid = null;
    }

    deskGroup = new THREE.Group();
    markRenderDirty?.();
    markShadowDirty?.();

    if (currentDeskSize === 'none') {
      floorGrid = new THREE.GridHelper(3500, 70, isLightMode ? 0x64748b : 0x334155, isLightMode ? 0xcbd5e1 : 0x1e293b);
      scene.add(floorGrid);
      return;
    }

    const [width, depth] = currentDeskSize.split('x').map(Number);
    const thickness = 25;
    const deskGeom = new THREE.BoxGeometry(width, thickness, depth);
    const deskMesh = new THREE.Mesh(
      deskGeom,
      new THREE.MeshStandardMaterial({ color: isLightMode ? 0xe2e8f0 : 0x242833, roughness: isLightMode ? 0.6 : 0.7 })
    );
    deskMesh.position.y = -thickness / 2;
    deskMesh.receiveShadow = true;
    deskGroup.add(deskMesh);

    const monitorGroup = new THREE.Group();
    const baseMesh = new THREE.Mesh(new THREE.BoxGeometry(220, 8, 180), new THREE.MeshStandardMaterial({ color: isLightMode ? 0x94a3b8 : 0x1a1d24, roughness: 0.5 }));
    baseMesh.position.set(0, 4, -depth/2 + 120);
    monitorGroup.add(baseMesh);

    const stemMesh = new THREE.Mesh(new THREE.BoxGeometry(42, 320, 26), new THREE.MeshStandardMaterial({ color: isLightMode ? 0x64748b : 0x1a1d24, roughness: 0.4, metalness: 0.25 }));
    stemMesh.position.set(0, 160, -depth/2 + 105);
    monitorGroup.add(stemMesh);

    const panelMesh = new THREE.Mesh(new THREE.BoxGeometry(615, 360, 16), new THREE.MeshStandardMaterial({ color: isLightMode ? 0x334155 : 0x0c0d12, roughness: 0.4 }));
    panelMesh.position.set(0, 300, -depth/2 + 115);
    monitorGroup.add(panelMesh);

    const screenFaceMesh = new THREE.Mesh(new THREE.BoxGeometry(603, 348, 1), new THREE.MeshStandardMaterial({ color: isLightMode ? 0xf8fafc : 0x050507, roughness: 0.95 }));
    screenFaceMesh.position.set(0, 300, -depth/2 + 124);
    monitorGroup.add(screenFaceMesh);

    deskGroup.add(monitorGroup);
    scene.add(deskGroup);
  }

  function toggleThemeMode() {
    const scene=getScene?.();
    if (!scene) return;
    isLightMode = !isLightMode;
    const iconContainer = document.getElementById('themeToggleIcon');
    const textContainer = document.getElementById('themeToggleText');
    const lights=getLights?.() || {};

    if (isLightMode) {
      document.documentElement.setAttribute('data-theme', 'light');
      if (iconContainer) iconContainer.innerHTML = DARK_ICON;
      if (textContainer) textContainer.innerText = 'DARK MODE';
      scene.background.setHex(0xf8fafc);
      if (scene.fog) scene.fog.color.setHex(0xf8fafc);
      if (lights.ambientLight) lights.ambientLight.intensity = 1.0;
      if (lights.hemiLight) lights.hemiLight.intensity = 0.8;
      if (lights.sunLight) lights.sunLight.intensity = 1.2;
      if (lights.headLight) lights.headLight.intensity = 0.6;
    } else {
      document.documentElement.removeAttribute('data-theme');
      if (iconContainer) iconContainer.innerHTML = LIGHT_ICON;
      if (textContainer) textContainer.innerText = 'LIGHT MODE';
      scene.background.setHex(0x050507);
      if (scene.fog) scene.fog.color.setHex(0x050507);
      if (lights.ambientLight) lights.ambientLight.intensity = 1.4;
      if (lights.hemiLight) lights.hemiLight.intensity = 1.0;
      if (lights.sunLight) lights.sunLight.intensity = 1.8;
      if (lights.headLight) lights.headLight.intensity = 1.4;
    }
    renderDeskSurfaceEnvironment();
  }

  function handleDeskEnvironmentChange(selectedValue) {
    currentDeskSize = selectedValue;
    renderDeskSurfaceEnvironment();
  }

  return { renderDeskSurfaceEnvironment, toggleThemeMode, handleDeskEnvironmentChange };
}
